import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  Observer,
  take,
  tap,
} from 'rxjs';

import { Module, UserPreferences, UserSession } from '@shared/models';
import {
  GenericHttpClientService,
  ModuleNavigationService,
  SelectedAccountService,
  SignOutService,
  SubprojectService,
  ToastNotificationService,
} from '@shared/services';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { HttpHeaders } from '@angular/common/http';
import { HttpSharedContext } from '@shared/enums';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserPreferencesDialogComponent } from '@shared/components';
import { Constants } from '@shared/utils';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  @Input({ required: true }) isHandset: boolean;

  isAppOnline$ = this.appFacade.query.isAppOnline$;
  isAppOnlineRealState$ = this.appFacade.query.isAppOnlineRealState$;
  isAppLockedInOffline$ = this.appFacade.query.isAppLockedInOffline$;
  selectedClient$ = this.userDetailsFacade.query.selectedClient$;
  baseAccount$ = this.userDetailsFacade.query.baseAccount$;

  userDetails: UserSession;
  shouldDisplaySubprojectPicker: boolean;
  shouldDisplayAppOnline: boolean;

  protected dialogRef?: MatDialogRef<any, any>;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private userDetailsFacade: UserDetailsFacade,
    private appFacade: AppFacade,
    private selectedAccountService: SelectedAccountService,
    private moduleNavigationService: ModuleNavigationService,
    private subprojectService: SubprojectService,
    private signOutService: SignOutService,
    private toastNotificationService: ToastNotificationService,
    private genericHttpClientService: GenericHttpClientService,
    private dialog: MatDialog
  ) {}

  //#region Lifecycle hooks

  ngOnInit(): void {
    this.setShouldDisplaySubprojectPicker(this.router.url);
    this.setShouldDisplayAppOnline(this.router.url);

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.url),
        tap((url) => {
          this.setShouldDisplaySubprojectPicker(url);
          this.setShouldDisplayAppOnline(url);
        })
      )
      .subscribe();

    this.userDetailsFacade.query.userDetails$
      .pipe(
        untilDestroyed(this),
        tap((userSession) => {
          this.userDetails = userSession;
        })
      )
      .subscribe();

    this.isAppOnline()
      .pipe(
        untilDestroyed(this),
        tap((isOnline) => {
          if (isOnline) {
            const interval = setInterval(() => {
              const headers = new HttpHeaders({
                timeout: `${1000}`,
              });
              this.genericHttpClientService
                .get<void>({
                  context: HttpSharedContext.SendHeartBeat,
                  headers,
                })
                .pipe(
                  untilDestroyed(this),
                  tap(() => {
                    this.setAppOnlineStatus(true);
                    clearInterval(interval);
                  })
                )
                .subscribe();
            }, 5000);
          } else {
            this.setAppOnlineStatus(false);
            this.toastNotificationService.warn(
              $localize`:@@app.header.txtNoInternetConnection:You have no internet connection.`
            );
          }
        })
      )
      .subscribe();
  }

  //#endregion

  toggleSidebar(): void {
    this.appFacade.toggleSidebar();
  }

  get isSigningOut(): boolean {
    return this.signOutService.isSigningOut;
  }

  setShouldDisplaySubprojectPicker(url: string): void {
    if (!url) {
      return;
    }
    this.shouldDisplaySubprojectPicker =
      url.includes('dprs') ||
      url.includes('project-setup') ||
      url.includes('dpr-details');
    this.cdr.markForCheck();
  }

  setShouldDisplayAppOnline(url: string): void {
    if (!url) {
      return;
    }
    this.shouldDisplayAppOnline =
      url.includes('dprs') || url.includes('dpr-details');
    this.cdr.markForCheck();
  }

  selectClient(clientId: string): void {
    this.selectedAccountService
      .selectClient(clientId)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  selectSubproject(subprojectId: number): void {
    this.subprojectService
      .selectSubproject(subprojectId, this.userDetails)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  updateUserPreferences(userPreferences: UserPreferences): void {
    this.genericHttpClientService
      .post<UserPreferences>({
        context: HttpSharedContext.SyncUserPreferences,
        body: userPreferences,
      })
      .pipe(
        untilDestroyed(this),
        tap((userPreferences) => {
          this.userDetailsFacade.setUserPreferences(userPreferences);
        })
      )
      .subscribe();
  }

  openUserPreferencesDialog(): void {
    this.isAppOnline$.pipe(take(1)).subscribe((isAppOnline) => {
      this.dialogRef = this.dialog.open(UserPreferencesDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          userPreferences: this.userDetails?.userPreferences,
          isAppOnline: isAppOnline,
          isHandset: this.isHandset,
        },
      });

      this.dialogRef.componentInstance.updateUserPreferences
        .pipe(untilDestroyed(this))
        .subscribe((userPreferences) =>
          this.updateUserPreferences(userPreferences)
        );
    });
  }

  selectModule(module: Module): void {
    this.moduleNavigationService.navigateToModule(module);
  }

  goToMyAccount(): void {
    this.moduleNavigationService.navigateToMyAccount();
  }

  signOut(): void {
    this.signOutService.signOut();
  }

  setAppLockedInOffline(appLockedInOffline: boolean): void {
    this.appFacade.setAppLockedInOffline(appLockedInOffline);
  }

  setAppOnlineStatus(isAppOnline: boolean): void {
    this.appFacade.setAppOnlineStatus(isAppOnline);
  }

  //#region Private methods

  private isAppOnline(): Observable<boolean> {
    return merge(
      fromEvent(window, 'offline').pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
      new Observable((sub: Observer<boolean>) => {
        sub.next(navigator.onLine);
        sub.complete();
      })
    );
  }

  //#endregion
}
